import React, { useCallback, useMemo, useState } from 'react';
import { FunctionBehaviorContainer, FunctionsLineChart } from './MessageQueuePage.styles';
import { GraphsData, GraphPoint, TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import { MessageQueue } from '@hud/portal-dtos/webapp-message-queue.dto';
import { useElementSize } from '@mantine/hooks';
import {
  formatBigNumbers,
  formatDateShort,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercentage,
  formatTime12Hour,
  formatTime12HourWithDay,
  isTimeFrameLessThan2Days,
  isTimeFrameLessThanDay,
} from '@hud/web-components/utils';
import { maxBy } from 'lodash';
import Metric from '@hud/web-components/Metric/Metric';
import { useTheme } from 'styled-components';
import { MetricHeader } from '@hud/web-components/Hud/FunctionBehaviorChart/MetricHeader';
import {
  DataKey,
  DataKeyDef,
  XReferenceLine,
} from '@hud/web-components/Charts/LineChart/LineChart';
import { FilterMenuButton } from '@hud/web-components/Hud/FunctionBehaviorChart/FilterMenuButton';
import { FlowDeployments } from '@hud/portal-dtos/webapp-endpoint.dto';
import {
  P90_TOOLTIP,
  P90_TOOLTIP_URL,
} from '@hud/web-components/Hud/P90InfoHover/P90InfoHover';
import {
  DeploymentsList,
  ReferenceIndicatorPopover,
} from '@hud/web-components/Hud/FunctionBehaviorChart/FunctionBehaviorChart.styles';
import ReferenceIndicator from '@hud/web-components/Charts/ReferenceIndicator/ReferenceIndicator';
import useTimeFrame from '../../../hooks/useTimeFrame';
import { useBooleanFeatureFlag } from '../../../hooks/useFeatureFlags';

export interface MessageQueueBehaviorProps {
  queue?: MessageQueue;
  graph?: GraphsData;
  deployments?: FlowDeployments;
  loading: boolean;
  compact?: boolean;
  timeFrame: TimeFrame;
  firstSeenAt?: Date;
}

const SINGLE_THRESHOLD_INTERVAL = 2;

const roundToBeginningOfHour = (date: Date): Date => {
  const newDate = new Date(date.getTime()); // Clone the original date
  newDate.setMinutes(0, 0, 0); // Sets minutes, seconds, and milliseconds to zero
  return newDate;
};

export default function MessageQueueBehavior({
  queue,
  graph,
  deployments,
  loading,
  compact = false,
  timeFrame,
  firstSeenAt,
}: MessageQueueBehaviorProps) {
  const theme = useTheme();
  const { ref, width } = useElementSize();
  const showDeployments = useBooleanFeatureFlag('show-deployments', true);

  const [hiddenDurationKeys, setHiddenDurationKeys] = useState<string[]>([]);
  const [hiddenConsumerDurationKeys, setHiddenConsumerDurationKeys] = useState<string[]>(
    [],
  );

  const { setTimeFrame } = useTimeFrame();
  const maxInvocations = maxBy(graph, 'runs')?.runs || 0;
  const maxDuration = Math.max(
    maxBy(graph, 'duration')?.duration || 0,
    maxBy(graph, 'durationP90')?.durationP90 || 0,
  );
  const maxErrorRate = maxBy(graph, 'errorRate')?.errorRate || 0;
  const maxConsumeDuration = Math.max(
    maxBy(graph, 'consumeDurationAvg')?.consumeDurationAvg || 0,
    maxBy(graph, 'consumeDurationP90')?.consumeDurationP90 || 0,
  );
  const maxMsgCount = maxBy(graph, 'msgCount')?.msgCount || 0;

  const isMaxInvocationsThreshold = maxInvocations < 0.01;
  const isMaxDurationThreshold = maxDuration < 0.00000001;
  const isMaxErrorRateThreshold = maxErrorRate < 0.01;
  const isMaxConsumeDurationThreshold = maxConsumeDuration < 0.00000001;
  const isMaxMsgCountThreshold = maxMsgCount < 0.01;

  const normalizedGraph = useMemo<GraphsData>(() => {
    const normalized =
      graph?.map((g) => {
        const firstSeenAt = queue?.firstSeenAt
          ? roundToBeginningOfHour(queue.firstSeenAt)
          : null;
        if (firstSeenAt && g.timestamp < firstSeenAt) {
          return {
            timestamp: g.timestamp,
            runs: null,
            duration: null,
            durationP90: null,
            errorRate: null,
            consumeDurationAvg: null,
            consumeDurationP90: null,
            msgCount: null,
          } satisfies GraphPoint;
        }
        return {
          timestamp: g.timestamp,
          runs: isMaxInvocationsThreshold
            ? g.runs === 0 || g.runs === null
              ? g.runs
              : maxInvocations
            : g.runs,
          duration: isMaxDurationThreshold
            ? g.duration === 0 || g.duration === null
              ? g.duration
              : maxDuration
            : g.duration,
          durationP90: isMaxDurationThreshold
            ? g.durationP90 === 0 || g.durationP90 === null || g.durationP90 === undefined
              ? g.durationP90
              : maxDuration
            : g.durationP90,
          errorRate: isMaxErrorRateThreshold
            ? g.errorRate === 0 || g.errorRate === null
              ? g.errorRate
              : maxErrorRate
            : g.errorRate,
          consumeDurationAvg: isMaxConsumeDurationThreshold
            ? g.consumeDurationAvg === 0 || g.consumeDurationAvg === null
              ? g.consumeDurationAvg
              : maxConsumeDuration
            : g.consumeDurationAvg,
          consumeDurationP90: isMaxConsumeDurationThreshold
            ? g.consumeDurationP90 === 0 || g.consumeDurationP90 === null
              ? g.consumeDurationP90
              : maxConsumeDuration
            : g.consumeDurationP90,
          msgCount: isMaxMsgCountThreshold
            ? g.msgCount === 0 || g.msgCount === null
              ? g.msgCount
              : maxMsgCount
            : g.msgCount,
        } satisfies GraphPoint;
      }) ?? [];
    return normalized;
  }, [
    queue,
    graph,
    isMaxDurationThreshold,
    isMaxErrorRateThreshold,
    isMaxInvocationsThreshold,
    isMaxConsumeDurationThreshold,
    maxDuration,
    maxErrorRate,
    maxInvocations,
    maxConsumeDuration,
    isMaxMsgCountThreshold,
    maxMsgCount,
  ]);

  const references = useMemo(() => {
    if (!graph || !deployments || graph.length < 2 || !showDeployments) return [];

    // Calculate the interval between the first two graph points
    const interval = graph[1].timestamp.getTime() - graph[0].timestamp.getTime();

    return graph
      .map((g) => {
        const relevantDeployments = deployments.filter((deployment) => {
          const deploymentTime = deployment.timestamp.getTime();
          const graphTime = g.timestamp.getTime();
          return deploymentTime >= graphTime && deploymentTime < graphTime + interval;
        });

        return {
          xAxisValue: g.timestamp,
          deployments: relevantDeployments,
        };
      })
      .filter((reference) => reference.deployments.length > 0); // Filter out empty references
  }, [deployments, graph, showDeployments]);

  function toggleHiddenDurationKey(key: string) {
    if (hiddenDurationKeys.includes(key)) {
      setHiddenDurationKeys(hiddenDurationKeys.filter((k) => k !== key));
    } else {
      setHiddenDurationKeys([...hiddenDurationKeys, key]);
    }
  }

  function toggleHiddenConsumerDurationKey(key: string) {
    if (hiddenConsumerDurationKeys.includes(key)) {
      setHiddenConsumerDurationKeys(hiddenConsumerDurationKeys.filter((k) => k !== key));
    } else {
      setHiddenConsumerDurationKeys([...hiddenConsumerDurationKeys, key]);
    }
  }

  const durationDataKeys: DataKey[] = [
    {
      key: 'duration',
      label: 'Avg Dur',
      hidden: hiddenDurationKeys.includes('duration'),
    },
    {
      key: 'durationP90',
      color: theme.purpleHome,
      label: 'P90 Dur',
      hidden: hiddenDurationKeys.includes('durationP90'),
    },
  ];

  const consumerDurationDataKeys: DataKey[] = [
    {
      key: 'consumeDurationAvg',
      label: 'Avg Dur',
      hidden: hiddenConsumerDurationKeys.includes('consumeDurationAvg'),
    },
    {
      key: 'consumeDurationP90',
      color: theme.purpleHome,
      label: 'P90 Dur',
      hidden: hiddenConsumerDurationKeys.includes('consumeDurationP90'),
    },
  ];

  const isLessThan2Days = isTimeFrameLessThan2Days(timeFrame, firstSeenAt);

  const renderReferenceLine = useCallback((value: XReferenceLine) => {
    const deployments = value.deployments as FlowDeployments;
    const functionsDeployed = deployments.reduce(
      (acc, deployment) => acc + deployment.functionIds.length,
      0,
    );
    const timestampDiv =
      deployments.length === 1 ? (
        <div>{formatDateTime(deployments[0].timestamp)}</div>
      ) : (
        <div>
          {formatDateTime(deployments[0].timestamp)} -{' '}
          {formatDateTime(deployments[deployments.length - 1].timestamp)}
        </div>
      );
    const deploymentMessage = `${functionsDeployed} function${functionsDeployed > 1 ? 's' : ''} deployed at`;
    return (
      <ReferenceIndicatorPopover
        body={
          <div>
            <div>{deploymentMessage}</div>
            {timestampDiv}
          </div>
        }
        clickBody={
          deployments.length > 1 ? (
            <DeploymentsList>
              {deployments.map((deployment, index) => (
                <div key={index}>{formatDateTime(deployment.timestamp)}</div>
              ))}
            </DeploymentsList>
          ) : null
        }
      >
        <ReferenceIndicator count={deployments.length} />
      </ReferenceIndicatorPopover>
    );
  }, []);

  const showE2EGraph =
    queue?.type === 'KAFKA' ||
    normalizedGraph.some(
      (point) => point.consumeDurationAvg != null && point.consumeDurationAvg > 0,
    );

  const showMsgCountGraph =
    queue?.type === 'SQS' ||
    normalizedGraph.some(
      (point) => point.msgCount != null && point.msgCount > 0,
    );

  const dataKeyDefs: DataKeyDef[] = [
    {
      title: 'Invocations',
      yInterval: isMaxInvocationsThreshold ? SINGLE_THRESHOLD_INTERVAL : undefined,
      labelFormatter: (value: number) => formatBigNumbers(value),
      tooltipFormatter: (value: number) => formatNumber(value),
      dataKeys: [{ key: 'runs' }],
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Total:',
              metric: <Metric metric={queue?.metrics.runs} />,
            },
            ...(!isTimeFrameLessThanDay(timeFrame, firstSeenAt)
              ? [{
                  title: 'Runs/Day:',
                  metric: (
                    <Metric
                      metric={queue?.metrics.normalizedRuns}
                      showTrend
                      trendTooltip="The trend change in the average number of messages enqueued per day - last 24h vs last 7d"
                    />
                  ),
                }]
              : []),
          ]}
        />
      ),
    },
    ...(showMsgCountGraph ? [{
      title: 'Messages',
      dataKeys: [{ key: 'msgCount' }],
      labelFormatter: (value: number) => formatBigNumbers(value),
      tooltipFormatter: (value: number) => formatNumber(value),
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Total:',
              metric: <Metric metric={queue?.metrics.msgCount} unit="abbreviated" />,
            },
            {
              title: 'Runs/Day:',
              metric: (
                <Metric
                  metric={queue?.metrics.normalizedMsgCount}
                  unit="abbreviated"
                />
              ),
            },
          ]}
        />
      ),
    }] : []),
    // NOTE!!!
    // the SDK sends the durations under different naming logic: the 'duration' metric is de-facto the consumer function duration after the message is pulled from the queue,
    // while the 'consume_duration' metric is the total e2e duration from the moment a message enters the queue until it is fully consumed.
    // This means that the 'consume_duration' is always longer than the 'duration'. This is confusing, so in the front end side we rename them to:
    // 'consume_duration' Metric in the BE -> 'e2e_duration' in the FE
    // 'duration' Metric in the BE -> 'consumer_duration' in the FE
    {
      title: (
        <>
          E2E Duration
          <FilterMenuButton
            dataKeys={consumerDurationDataKeys}
            hiddenKeys={hiddenConsumerDurationKeys}
            onToggle={toggleHiddenConsumerDurationKey}
          />
        </>
      ),
      dataKeys: consumerDurationDataKeys,
      yInterval: isMaxDurationThreshold ? SINGLE_THRESHOLD_INTERVAL : undefined,
      labelFormatter: (value: number) => formatDuration(value, 0),
      tooltipFormatter: (value: number) => formatDuration(value, 0),
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Avg:',
              color: theme.turquoise,
              metric: (
                <Metric
                  metric={queue?.metrics.consumeDurationAvg}
                  unit="duration"
                  showTrend
                  trendTooltip="The trend change in the average time taken for a message to be processed from entry to consumption - last 24h vs last 7d"
                />
              ),
            },
            {
              title: 'P90:',
              tooltip: P90_TOOLTIP,
              tooltipUrl: P90_TOOLTIP_URL,
              color: theme.purpleHome,
              metric: (
                <Metric
                  metric={queue?.metrics.consumeDurationP90}
                  unit="duration"
                  showTrend
                  trendTooltip="The trend change in the P90 of end-to-end message processing times - last 24h vs last 7d"
                />
              ),
            },
          ]}
        />
      ),
    },
    {
      title: (
        <>
          Processing Duration
          <FilterMenuButton
            dataKeys={durationDataKeys}
            hiddenKeys={hiddenDurationKeys}
            onToggle={toggleHiddenDurationKey}
          />
        </>
      ),
      dataKeys: durationDataKeys,
      yInterval: isMaxDurationThreshold ? SINGLE_THRESHOLD_INTERVAL : undefined,
      labelFormatter: (value: number) => formatDuration(value, 0),
      tooltipFormatter: (value: number) => formatDuration(value, 0),
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Avg:',
              color: theme.turquoise,
              hidden: hiddenDurationKeys.includes('duration'),
              metric: (
                <Metric
                  metric={queue?.metrics.durationAvg}
                  unit="duration"
                  showTrend
                  trendTooltip="The trend change in the average time taken for a message to be processed after being pulled from the queue or topic - last 24h vs last 7d"
                />
              ),
            },
            {
              title: 'P90:',
              tooltip: P90_TOOLTIP,
              tooltipUrl: P90_TOOLTIP_URL,
              color: theme.purpleHome,
              hidden: hiddenDurationKeys.includes('durationP90'),
              metric: (
                <Metric
                  metric={queue?.metrics.durationP90}
                  unit="duration"
                  showTrend
                  trendTooltip="The trend change in the P90 of processing times after a message is pulled from the queue -  last 24h vs last 7d"
                />
              ),
            },
          ]}
        />
      ),
    },
    {
      title: 'Error Rate',
      dataKeys: [{ key: 'errorRate' }],
      domain: [0, maxErrorRate === 0 ? 1 : 'auto'],
      labelFormatter: (value: number) => formatPercentage(value),
      tooltipFormatter: (value: number) => formatPercentage(value),
      headerComponent: (
        <MetricHeader
          loading={loading}
          metrics={[
            {
              title: 'Rate:',
              color: theme.turquoise,
              metric: (
                <Metric
                  metric={queue?.metrics.errorRate}
                  unit="percent"
                  showTrend
                  trendTooltip="The trend change in the percentage of messages that resulted in a 5xx error - last 24h vs last 7d"
                />
              ),
            },
          ]}
        />
      ),
    },
  ];

  if (!showE2EGraph) {
    dataKeyDefs.splice(1, 1); // remove e2e duration if no data
  }

  return (
    <FunctionBehaviorContainer ref={ref}>
      <FunctionsLineChart
        compact={compact}
        graphId="messageQueueBehaviorChart"
        width={width}
        headerWidth={210}
        height={100}
        interval={isLessThan2Days ? undefined : 23}
        data={normalizedGraph}
        labelFormatter={(value, index) => {
          if (isLessThan2Days) {
            if (index === 0) {
              return formatTime12HourWithDay(new Date(value));
            }
            return formatTime12Hour(new Date(value));
          }
          return formatDateShort(new Date(value));
        }}
        renderReference={renderReferenceLine}
        dataKeyDefs={dataKeyDefs}
        XdataKey="timestamp"
        referenceLines={references}
        onRangeSelected={setTimeFrame}
      />
    </FunctionBehaviorContainer>
  );
}
